import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ActivitySummary from './ActivitySummary';
import ContributionCalendar from './ContributionCalendar';
import ProjectActivityFeed from './ProjectActivityFeed';
import { useProjectActivity } from './useProjectActivity';

export default function ProjectActivitySection() {
  const activity = useProjectActivity();
  const currentYear = new Date().getUTCFullYear();
  const years = Array.from(
    new Set([
      currentYear,
      activity.year,
      ...(activity.summary?.availableYears ?? []),
    ])
  ).sort((a, b) => b - a);

  return (
    <Box component="section" aria-labelledby="project-activity-heading">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h5" id="project-activity-heading" gutterBottom>
            Project Activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Progress from project versions you successfully published.
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 112 }}>
          <InputLabel id="activity-year-label">Year</InputLabel>
          <Select
            labelId="activity-year-label"
            label="Year"
            value={activity.year}
            onChange={(event) => activity.setYear(Number(event.target.value))}
          >
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {activity.summaryError && !activity.summary ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={() => activity.retrySummary()}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Could not load the activity calendar. Your account settings are still
          available.
        </Alert>
      ) : (
        <Stack gap={1.5}>
          <ActivitySummary
            loading={activity.summaryLoading}
            values={activity.summary}
          />
          <ContributionCalendar
            year={activity.year}
            days={activity.summary?.days ?? []}
            total={activity.summary?.totalContributions ?? 0}
            loading={activity.summaryLoading}
          />
        </Stack>
      )}

      <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
        Recent Activity
      </Typography>
      <ProjectActivityFeed
        items={activity.items}
        loading={activity.feedLoading}
        error={activity.feedError}
        hasMore={activity.hasMore}
        onLoadMore={activity.loadMore}
        onRetry={() => activity.retryFeed()}
      />
    </Box>
  );
}
